import { useDemo } from "@/components/ui/demo-provider";

/**
 * Phase 11 - Site footer
 * Emergent structure adapted to SPA hash nav; only real destinations.
 * Privacy/Terms omitted - no dedicated routes yet (no # placeholders / mailto stand-ins).
 */

const NAV = [
  { label: "Platform", href: "/#platform" },
  { label: "Payroll", href: "/#payroll" },
  { label: "Analysis", href: "/#analysis" },
  { label: "Pack", href: "/#month-end" },
  { label: "Workforce", href: "/#workforce" },
  { label: "Architecture", href: "/#architecture" },
  { label: "Spectrum", href: "/#spectrum" },
  { label: "Security", href: "/#security" },
];

/** Oversized closing wordmark - single Clash Display string (no frankenstein SVG Q). */
function FooterWordmark() {
  return (
    <div
      className="pointer-events-none relative z-0 select-none overflow-visible px-3 pb-3 pt-6 sm:px-6"
      aria-hidden="true"
      data-testid="footer-wordmark"
    >
      <p
        className="mx-auto block w-full text-center font-display text-[clamp(4rem,24vw,19rem)] font-semibold leading-[0.95] tracking-[0.08em] text-white/[0.02]"
        style={{
          paddingBottom: "0.14em",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        RORAQ
      </p>
    </div>
  );
}

export default function Footer() {
  const demo = useDemo();

  return (
    <footer
      className="relative overflow-x-clip border-t border-[color:var(--border-subtle)]"
      style={{
        background:
          "linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-footer) 42%, var(--bg-footer) 100%)",
      }}
      data-testid="site-footer"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <a href="/" className="inline-flex items-center" data-testid="footer-logo" aria-label="RORAQ home">
              <img
                src="/brand/lockup-white.png"
                alt="RORAQ"
                className="h-9 w-auto"
                draggable={false}
              />
            </a>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
              The operational layer around payroll.
            </p>
          </div>

          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">Navigation</p>
            <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 sm:grid-cols-2">
              {NAV.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="inline-flex min-h-10 items-center text-sm text-slate-400 transition-colors duration-300 hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-footer)]"
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">Actions</p>
            <ul className="mt-4 space-y-2.5">
              <li>
                <button
                  type="button"
                  onClick={demo.open}
                  className="inline-flex min-h-11 items-center text-sm text-slate-400 transition-colors duration-300 hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-footer)]"
                  data-testid="footer-request-demo"
                >
                  Request a demo
                </button>
              </li>
              <li>
                <a
                  href="https://app.roraq.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center text-sm text-slate-400 transition-colors duration-300 hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-footer)]"
                  data-testid="footer-login"
                >
                  Log in
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <FooterWordmark />

      <div className="relative z-10 border-t border-white/[0.05]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-5 sm:flex-row sm:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">
            © 2026 RORAQ - Operational layer around payroll
          </p>
          <a
            href="https://app.roraq.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600 transition-colors hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50"
          >
            app.roraq.com
          </a>
        </div>
      </div>
    </footer>
  );
}
